import React, { useEffect, useState } from 'react';
import { getFirestore, collection, query, where, addDoc, getDocs, doc, getDoc, setDoc, updateDoc, orderBy, limit } from 'firebase/firestore';
import { useAuth } from "../context/AuthContext.js";

const Solicitudes = () => {
  const { currentUser } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [userProfession, setUserProfession] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [presupuesto, setPresupuesto] = useState('');
  const [consulta, setConsulta] = useState('');
  const [showPresupuestoForm, setShowPresupuestoForm] = useState(false);
  const [showConsultaForm, setShowConsultaForm] = useState(false);
  const [hasAssignedLimitReached, setHasAssignedLimitReached] = useState(false);

  useEffect(() => {
    if (currentUser?.uid) {
      const db = getFirestore();
      const userRef = doc(db, "users", currentUser.uid);

      const getUserProfession = async () => {
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserProfession(userData.profession);
          } else {
            console.log("El documento del usuario no existe");
          }
        } catch (error) {
          console.error("Error al obtener la profesión del usuario:", error);
        }
      };

      getUserProfession();
    }
  }, [currentUser]);

  useEffect(() => {
    if (userProfession && currentUser?.uid) {
      const db = getFirestore();
      const userRef = doc(db, "users", currentUser.uid);
  
      const fetchSolicitudes = async () => {
        try {
          const userDoc = await getDoc(userRef);
          if (!userDoc.exists()) {
            console.error("El documento del usuario no existe.");
            return;
          }
  
          const { country, state, city} = userDoc.data(); // Extraer ubicación del profesional
  
          const q = query(
            collection(db, "solicitudservicios"),
            where("category", "==", userProfession),
            where("country", "==", country), 
            where("state", "==", state), 
            where("city", "==", city),
            orderBy("createdAt", "desc") // Ordena de más reciente a más antigua
          );
  
          const querySnapshot = await getDocs(q);
          const solicitudesData = [];
  
          for (const docSnap of querySnapshot.docs) {
            const profesionalRef = doc(docSnap.ref, "profesionales", currentUser.uid);
            const profesionalDoc = await getDoc(profesionalRef);
  
            if (!profesionalDoc.exists() || profesionalDoc.data().estado !== "rechazada") {
              solicitudesData.push({ id: docSnap.id, ...docSnap.data() });
            }
          }
  
          setSolicitudes(solicitudesData);
  
          // Verificar si se ha alcanzado el límite de solicitudes "asignadas"
          const assignedSolicitudes = solicitudesData.filter(solicitud => solicitud.estado === "asignado");
          setHasAssignedLimitReached(assignedSolicitudes.length >= 3);
  
        } catch (error) {
          console.error("Error al obtener solicitudes:", error);
        }
      };
  
      fetchSolicitudes();
    }
  }, [userProfession, currentUser]);
  

  const handleVerSolicitud = (id) => {
    const solicitud = solicitudes.find((solicitud) => solicitud.id === id);
    setSelectedSolicitud(solicitud);
    setModalOpen(true);
  };

  const handleCerrarModal = () => {
    setModalOpen(false);
    setPresupuesto('');
    setConsulta('');
    setShowPresupuestoForm(false);
    setShowConsultaForm(false);
  };

  const handleRechazar = async () => {
    const db = getFirestore();
    const solicitudRef = doc(db, "solicitudservicios", selectedSolicitud.id);
    const profesionalRef = doc(solicitudRef, "profesionales", currentUser.uid);
  
    await setDoc(profesionalRef, {
      estado: "rechazada",
      createdAt: new Date().toISOString(),
    });
  
    setSolicitudes((prevSolicitudes) =>
      prevSolicitudes.filter((solicitud) => solicitud.id !== selectedSolicitud.id)
    );
  
    setModalOpen(false);
  };

  const handleFinalizarTrabajo = async () => {
    const db = getFirestore();
    const solicitudRef = doc(db, "solicitudservicios", selectedSolicitud.id);
  
    await updateDoc(solicitudRef, {
      estado: "finalizado",
      finalizadoAt: new Date().toISOString(),
    });

    // Actualiza el estado local de las solicitudes
    setSolicitudes((prevSolicitudes) =>
      prevSolicitudes.map((solicitud) =>
        solicitud.id === selectedSolicitud.id ? { ...solicitud, estado: "finalizado" } : solicitud
      )
    );

    setModalOpen(false);
  };

  const handleEnviarPresupuesto = async () => {
    const db = getFirestore();
    const solicitudRef = doc(db, "solicitudservicios", selectedSolicitud.id);
    const profesionalRef = doc(solicitudRef, "profesionales", currentUser.uid);
  
    await setDoc(profesionalRef, {
      estado: "presupuesto_enviado",
      presupuesto: presupuesto,
      createdAt: new Date().toISOString(),
    });
  
    setModalOpen(false);
  };

  const handleEnviarConsulta = async () => {
    const db = getFirestore();
  
    try {
      const solicitudRef = doc(db, "solicitudservicios", selectedSolicitud.id);
      const solicitudSnapshot = await getDoc(solicitudRef);
  
      if (solicitudSnapshot.exists()) {
        const solicitudData = solicitudSnapshot.data();
  
        const usuarioRef = doc(db, "users", solicitudData.uid);
        const usuarioSnapshot = await getDoc(usuarioRef);
        const nombreUsuario = usuarioSnapshot.exists()
          ? usuarioSnapshot.data().fullName
          : "Usuario sin nombre";
  
        const profesionalRef = doc(db, "users", currentUser.uid);
        const profesionalSnapshot = await getDoc(profesionalRef);
        const nombreProfesional = profesionalSnapshot.exists()
          ? profesionalSnapshot.data().fullName
          : "Profesional sin nombre";
  
        const mensajesRef = collection(db, "mensajes");
        await addDoc(mensajesRef, {
          fechaHora: new Date(),
          emisor: {
            uid: currentUser.uid,
            nombre: nombreProfesional,
          },
          receptor: {
            uid: solicitudData.uid,
            nombre: nombreUsuario,
          },
          asunto: solicitudData.description || "Sin asunto",
          mensaje: consulta,
          idSolicitud: selectedSolicitud.id,
        });
  
        console.log("Consulta enviada correctamente");
        setModalOpen(false);
      } else {
        console.error("No se encontró la solicitud seleccionada.");
      }
    } catch (error) {
      console.error("Error al enviar la consulta:", error);
    }
  };

  return (
    <div className="solicitudes-tab">
      <h3>Solicitudes de Presupuestos</h3>
      <table className="table">
        <thead>
          <tr>
            <th>Cliente</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {solicitudes.filter(solicitud => solicitud.estado !== 'pendiente' || !hasAssignedLimitReached).map((solicitud) => (
            <tr key={solicitud.id}>
              <td>{solicitud.cliente}</td>
              <td>{solicitud.estado}</td>
              <td>
                <button
                  className="btn btn-primary"
                  onClick={() => handleVerSolicitud(solicitud.id)}
                >
                  Ver
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {hasAssignedLimitReached && (
        <div className="alert alert-warning">
          Has alcanzado el límite de solicitudes asignadas. Finaliza un trabajo para seguir recibiendo solicitudes.
        </div>
      )}

      {modalOpen && selectedSolicitud && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Descripción de la solicitud</h4>
            <p>{selectedSolicitud.description}</p>

            <div className="modal-buttons">
              {selectedSolicitud.estado === "pendiente" && (
                <>
                  <button className="btn btn-danger" onClick={handleRechazar}>Rechazar</button>
                  <button className="btn btn-danger" onClick={() => setShowPresupuestoForm(!showPresupuestoForm)}>
                    {showPresupuestoForm ? 'Cancelar presupuesto' : 'Enviar presupuesto'}
                  </button>
                  <button className="btn btn-danger" onClick={() => setShowConsultaForm(!showConsultaForm)}>
                    {showConsultaForm ? 'Cancelar consulta' : 'Preguntar'}
                  </button>
                </>
              )}

              {selectedSolicitud.estado === "asignado" && (
                <button className="btn btn-success" onClick={handleFinalizarTrabajo}>Finalizar trabajo</button>
              )}

            </div>

            {showPresupuestoForm && (
              <div>
                <label>Presupuesto</label>
                <textarea value={presupuesto} onChange={(e) => setPresupuesto(e.target.value)} />
                <button className="btn btn-success" onClick={handleEnviarPresupuesto}>Enviar presupuesto</button>
              </div>
            )}

            {showConsultaForm && (
              <div>
                <label>Consulta</label>
                <textarea value={consulta} onChange={(e) => setConsulta(e.target.value)} />
                <button className="btn btn-danger" onClick={handleEnviarConsulta}>Enviar consulta</button>
              </div>
            )}

            <button className="btn btn-secondary" onClick={handleCerrarModal}>Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Solicitudes;
